// Registers the service worker (production builds only) and resolves once it
// controls this page, i.e. once every runtime file is in its cache. Until then
// the app is not "ready": a request after that point is answered locally.
// A controlled page always runs its worker's version (navigations come from
// the worker's cache), so nothing needs to wait for a newer version, which
// installs in the background and takes over once all FlexPDF tabs are closed.
export async function offlineReady(): Promise<void> {
  if (!import.meta.env.PROD) return;
  if (!('serviceWorker' in navigator)) throw new Error('this browser does not support service workers, which FlexPDF needs to work without the network');
  const sw = navigator.serviceWorker;
  const controlled = new Promise<void>((resolve) => sw.addEventListener('controllerchange', () => resolve(), { once: true }));
  const reg = await sw.register(new URL('sw.js', document.baseURI), { scope: './', updateViaCache: 'none' });
  if (sw.controller) return;
  if (!reg.active) {
    // First visit: the worker is precaching; it takes over this page when it activates.
    const installing = reg.installing ?? reg.waiting;
    await new Promise<void>((resolve, reject) => {
      void controlled.then(resolve);
      installing?.addEventListener('statechange', () => installing.state === 'redundant' && reject(new Error('the offline cache could not be filled')));
    });
    return;
  }
  // Loaded past an active worker (a hard reload): it may take over this page only if it is this page's version.
  const script = document.querySelector<HTMLScriptElement>('script[type="module"][src]')?.src;
  const answer = new Promise<boolean>((resolve) => {
    const onMessage = (e: MessageEvent<{ type?: string; ok?: boolean }>) => {
      if (e.data?.type !== 'claim') return;
      sw.removeEventListener('message', onMessage);
      resolve(!!e.data.ok);
    };
    sw.addEventListener('message', onMessage);
  });
  reg.active.postMessage({ type: 'claim', script });
  if (!(await answer)) throw new Error('FlexPDF was updated while another FlexPDF tab was open. Close the other FlexPDF tabs, then reload this one');
  await controlled;
}
